import React from 'react';
import { Icon } from 'semantic-ui-react';
import './TryOnLoadingIndicator.css';

/**
 * Loading indicator component for virtual try-on operations
 * Shows progress, status messages, and provides user feedback during processing
 */
const TryOnLoadingIndicator = ({ 
  isLoading = false, 
  progress = 0, 
  message = 'Processing...', 
  showCancel = false,
  onCancel = null,
  variant = 'default' // 'default', 'minimal', 'detailed'
}) => {
  if (!isLoading) return null;

  const getProgressMessage = (progress) => {
    if (progress < 20) return 'Validating images...';
    if (progress < 40) return 'Checking availability...';
    if (progress < 60) return 'Optimizing images...';
    if (progress < 80) return 'Processing virtual try-on...';
    if (progress < 100) return 'Finalizing result...';
    return 'Complete!';
  };

  const getEstimatedTime = (progress) => {
    if (progress < 20) return '~15 seconds remaining';
    if (progress < 40) return '~12 seconds remaining';
    if (progress < 60) return '~8 seconds remaining';
    if (progress < 80) return '~5 seconds remaining';
    if (progress < 100) return '~2 seconds remaining';
    return 'Done!';
  };

  if (variant === 'minimal') {
    return (
      <div className="try-on-loading-minimal">
        <div className="loading-spinner-small"></div>
        <span className="loading-text-small">{message}</span>
      </div>
    );
  }

  return (
    <div className={`try-on-loading-overlay ${variant}`}>
      <div className="try-on-loading-container">
        {/* Main Loading Animation */}
        <div className="loading-animation">
          <div className="loading-spinner"></div>
          <div className="loading-pulse"></div>
        </div>

        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
            ></div>
          </div>
          <div className="progress-text">{Math.round(progress)}%</div>
        </div>

        {/* Status Messages */}
        <div className="status-messages">
          <div className="primary-message">{message}</div>
          {variant === 'detailed' && (
            <>
              <div className="secondary-message">{getProgressMessage(progress)}</div>
              <div className="time-estimate">{getEstimatedTime(progress)}</div>
            </>
          )}
        </div>

        {/* Disclaimer */}
        <div className="vto-disclaimer">
          <p>Disclaimer: AI distorts your body and face, the more you layer on the more distortion it creates, the results are for reference, fun, and creative inspiration only not to replace real life fitting</p>
        </div>

        {/* Tips and Information */}
        {variant === 'detailed' && progress < 80 && (
          <div className="loading-tips">
            <div className="tip-icon"><Icon name="lightbulb outline" style={{ margin: 0 }} /></div>
            <div className="tip-text">
              {progress < 40 
                ? "For best results, use a clear photo with good lighting"
                : "AI is analyzing the fit and positioning of your garment"
              }
            </div>
          </div>
        )}

        {/* Cancel Button */}
        {showCancel && onCancel && (
          <button 
            className="cancel-button"
            onClick={onCancel}
            disabled={progress > 80}
          >
            Cancel
          </button>
        )}

        {/* Processing Steps Indicator */}
        {variant === 'detailed' && (
          <div className="processing-steps">
            <div className={`step ${progress >= 20 ? 'completed' : progress >= 10 ? 'active' : ''}`}>
              <div className="step-icon"><Icon name="check" style={{ margin: 0 }} /></div>
              <div className="step-label">Validate</div>
            </div>
            <div className={`step ${progress >= 40 ? 'completed' : progress >= 20 ? 'active' : ''}`}>
              <div className="step-icon"><Icon name="check" style={{ margin: 0 }} /></div>
              <div className="step-label">Optimize</div>
            </div>
            <div className={`step ${progress >= 80 ? 'completed' : progress >= 40 ? 'active' : ''}`}>
              <div className="step-icon"><Icon name="check" style={{ margin: 0 }} /></div>
              <div className="step-label">Process</div>
            </div>
            <div className={`step ${progress >= 100 ? 'completed' : progress >= 80 ? 'active' : ''}`}>
              <div className="step-icon"><Icon name="check" style={{ margin: 0 }} /></div>
              <div className="step-label">Complete</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Simplified loading spinner for inline use
 */
export const LoadingSpinner = ({ size = 'medium', color = '#007bff' }) => {
  const sizeClass = `spinner-${size}`;
  
  return (
    <div className={`inline-spinner ${sizeClass}`}>
      <div 
        className="spinner-circle" 
        style={{ borderTopColor: color }}
      ></div>
    </div>
  );
};

/**
 * Progress bar component for standalone use
 */
export const ProgressBar = ({ 
  progress = 0, 
  showPercentage = true, 
  height = '8px',
  color = '#007bff',
  backgroundColor = '#e9ecef'
}) => {
  return (
    <div className="standalone-progress-container">
      <div 
        className="standalone-progress-bar"
        style={{ height, backgroundColor }}
      >
        <div 
          className="standalone-progress-fill"
          style={{ 
            width: `${Math.max(0, Math.min(100, progress))}%`,
            backgroundColor: color
          }}
        ></div>
      </div>
      {showPercentage && (
        <div className="standalone-progress-text">
          {Math.round(progress)}%
        </div>
      )}
    </div>
  );
};

/**
 * Status message component with icon
 */
export const StatusMessage = ({ 
  type = 'info', // 'info', 'success', 'warning', 'error'
  message, 
  icon = null,
  onClose = null 
}) => {
  const getDefaultIcon = (type) => {
    switch (type) {
      case 'success': return <Icon name="check circle" color="green" style={{ margin: 0 }} />;
      case 'warning': return <Icon name="warning sign" color="yellow" style={{ margin: 0 }} />;
      case 'error': return <Icon name="times circle" color="red" style={{ margin: 0 }} />;
      default: return <Icon name="info circle" color="blue" style={{ margin: 0 }} />;
    }
  };

  return (
    <div className={`status-message status-${type}`}>
      <div className="status-icon">
        {icon || getDefaultIcon(type)}
      </div>
      <div className="status-text">{message}</div>
      {onClose && (
        <button className="status-close" onClick={onClose}>
          ×
        </button>
      )}
    </div>
  );
};

export default TryOnLoadingIndicator;